import { apiClient } from './client';
import { CursorPage } from '@/types';

export interface Notification {
  id: string;
  userId: string;
  type: 'MENTION' | 'DM' | 'CHANNEL_INVITE' | 'SYSTEM';
  title: string;
  body: string;
  metadata: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

export const notificationsApi = {
  async list(params?: { cursor?: string; limit?: number; unreadOnly?: boolean }) {
    const res = await apiClient.get<CursorPage<Notification>>('/api/notifications', { params });
    return res.data;
  },

  async unreadCount(): Promise<number> {
    const res = await apiClient.get<{ count: number }>('/api/notifications/unread-count');
    return res.data.count;
  },

  async markRead(id: string) {
    await apiClient.put(`/api/notifications/${id}/read`);
  },

  async markAllRead() {
    await apiClient.put('/api/notifications/read-all');
  },

  async remove(id: string) {
    await apiClient.delete(`/api/notifications/${id}`);
  },

  // ── v5.0 — Per-type notification preferences (backend Phase P3 / v4.2) ───
  // Backend stores a free-form JSON map: { types?: { DM: false, ... }, quietHours?: {start,end,tz}? }
  // null = всё включено по умолчанию. PATCH мерджится с существующим.
  async getPrefs(): Promise<{ prefs: NotificationPrefs }> {
    const res = await apiClient.get('/api/notifications/prefs');
    return res.data;
  },

  async patchPrefs(patch: {
    types?: Partial<Record<NotificationTypeKey, boolean>>;
    quietHours?: { start: number; end: number; tz?: string } | null;
  }): Promise<{ prefs: NotificationPrefs }> {
    const res = await apiClient.patch('/api/notifications/prefs', patch);
    return res.data;
  },
};

/** All notification types the backend currently emits (extend as new types are added). */
export type NotificationTypeKey =
  | 'DM'
  | 'MENTION'
  | 'CHANNEL_INVITE'
  | 'CALL_INCOMING'
  | 'CALL_MISSED'
  | 'STORY_LIKE'
  | 'STORY_COMMENT'
  | 'STORY_REACTION'
  | 'WALL_LIKE'
  | 'WALL_COMMENT'
  | 'FOLLOW'
  | 'FRIEND_REQUEST'
  | 'FRIEND_ACCEPTED'
  | 'KARMA_GAIN'
  | 'SYSTEM';

export interface NotificationPrefs {
  // Per-type opt-out: missing key = enabled. Explicit false = silenced.
  types?: Partial<Record<NotificationTypeKey, boolean>>;
  // Quiet hours window. start/end in 0..23, tz IANA-name (default Europe/Moscow).
  // If start > end the window crosses midnight (e.g. 22 → 7).
  quietHours?: { start: number; end: number; tz?: string };
}
